import { useEffect, useState } from "preact/hooks";
import { api } from "../api";
import { Sheet } from "./Sheet";
import type { DayLogs } from "@hh/shared";

type Active = null | "caffeine" | "alcohol" | "mood";

const CAFFEINE = [
  { label: "Coffee", mg: 95, src: "coffee" },
  { label: "Espresso", mg: 63, src: "espresso" },
  { label: "Tea", mg: 47, src: "tea" },
  { label: "Energy", mg: 80, src: "energy drink" },
];
const DRINKS = [
  { label: "Wine (175ml)", type: "wine", units: 2.1 },
  { label: "Beer (pint)", type: "beer", units: 2.3 },
  { label: "Spirit (shot)", type: "spirit", units: 1 },
  { label: "½ bottle wine", type: "wine", units: 5 },
];

export function Wellness({ reloadKey, onChanged }: { reloadKey: number; onChanged: () => void }) {
  const [d, setD] = useState<DayLogs | null>(null);
  const [active, setActive] = useState<Active>(null);

  function load() { api.logsToday().then(setD).catch(() => {}); }
  useEffect(load, [reloadKey]);

  async function addWater() { await api.water({ ml: 250 }); load(); onChanged(); }
  async function addCaffeine(mg: number, src: string) { await api.caffeine({ mg, source: src }); setActive(null); load(); onChanged(); }
  async function addAlcohol(type: string, units: number) { await api.alcohol({ type, units }); setActive(null); load(); onChanged(); }
  async function setMood(v: number) { await api.mood({ mood: v }); setActive(null); load(); onChanged(); }

  const waterPct = d ? Math.min(100, Math.round((d.water_ml / d.water_target_ml) * 100)) : 0;

  return (
    <div class="card">
      <h2>Wellness</h2>
      <div class="well-grid">
        <button class="well-tile" onClick={addWater}>
          <div class="ico">💧</div>
          <div class="v">{d ? `${(d.water_ml / 1000).toFixed(1)}L` : "—"}</div>
          <div class="l">+250ml</div>
        </button>
        <button class="well-tile" onClick={() => setActive("caffeine")}>
          <div class="ico">☕</div>
          <div class="v">{d ? `${d.caffeine_mg}mg` : "—"}</div>
          <div class="l">caffeine</div>
        </button>
        <button class="well-tile" onClick={() => setActive("alcohol")}>
          <div class="ico">🍷</div>
          <div class="v">{d ? `${d.alcohol_units}u` : "—"}</div>
          <div class="l">alcohol</div>
        </button>
        <button class="well-tile" onClick={() => setActive("mood")}>
          <div class="ico">{d?.mood?.mood ? ["😢", "🙁", "😐", "🙂", "😄"][d.mood.mood - 1] : "🙂"}</div>
          <div class="v">{d?.mood?.mood ? `${d.mood.mood}/5` : "—"}</div>
          <div class="l">mood</div>
        </button>
      </div>
      <div class="water-bar"><span style={{ width: `${waterPct}%` }} /></div>
      {d && d.alcohol_units > 0 && (
        <p class="muted" style={{ marginTop: 6 }}>🍷 {d.alcohol_units} units (~{d.alcohol_kcal} kcal) — counts against hydration; wind down earlier tonight.</p>
      )}

      {active === "caffeine" && (
        <Sheet title="Add caffeine" onClose={() => setActive(null)}>
          <div class="opt-row">
            {CAFFEINE.map((c) => <button key={c.label} onClick={() => addCaffeine(c.mg, c.src)}>{c.label} · {c.mg}mg</button>)}
          </div>
        </Sheet>
      )}
      {active === "alcohol" && (
        <Sheet title="Log a drink" onClose={() => setActive(null)}>
          <div class="opt-row">
            {DRINKS.map((dr) => <button key={dr.label} onClick={() => addAlcohol(dr.type, dr.units)}>{dr.label}</button>)}
          </div>
          <p class="muted">Logged drinks count against hydration and feed your wind-down nudge.</p>
        </Sheet>
      )}
      {active === "mood" && (
        <Sheet title="How are you feeling?" onClose={() => setActive(null)}>
          <div class="opt-row">
            {[1, 2, 3, 4, 5].map((v) => (
              <button key={v} onClick={() => setMood(v)} style={{ fontSize: 22, width: 52 }}>
                {["😢", "🙁", "😐", "🙂", "😄"][v - 1]}
              </button>
            ))}
          </div>
        </Sheet>
      )}
    </div>
  );
}
